import {ICar, ICarMilestone, IMilestoneDef, IStage, IUserBrief, DOOR_CALIBRATION, REWORK_STAGE} from "./types";
import {TX} from "./texts";

const pad = (n: number) => String(n).padStart(2, "0");

/** «12 мин», «1 ч 05 мин», «3 ч». */
export const formatDuration = (fromIso: string, now: number): string => {
    const minutes = Math.max(0, Math.floor((now - Date.parse(fromIso)) / 60000));
    if (minutes < 60) return `${minutes} ${TX.min}`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `${h} ${TX.hour} ${pad(m)} ${TX.min}` : `${h} ${TX.hour}`;
};

/** Рабочее время в Стате (S17.2): «45 м», «9 ч 40 м», «53 ч 05 м»; 0 — «—». */
export const formatMs = (ms: number | null | undefined): string => {
    if (!ms || ms <= 0) return "—";
    const minutes = Math.round(ms / 60000);
    if (minutes < 60) return `${minutes} ${TX.minShort}`;
    return `${Math.floor(minutes / 60)} ${TX.hour} ${pad(minutes % 60)} ${TX.minShort}`;
};

const dayStart = (ts: number) => {
    const d = new Date(ts);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

export const formatClock = (iso: string): string => {
    const d = new Date(iso);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** «сегодня 09:40», «вчера 18:05», «22.10 18:05». */
export const formatMoment = (iso: string, now: number): string => {
    const ts = Date.parse(iso);
    const today = dayStart(now);
    const clock = formatClock(iso);
    if (ts >= today) return `${TX.todayLower} ${clock}`;
    if (ts >= today - 86400000) return `${TX.yesterday} ${clock}`;
    const d = new Date(ts);
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${clock}`;
};

/** Заголовок дня в журнале: «Сегодня», «Вчера», «22.10.2026». */
export const formatDay = (iso: string, now: number): string => {
    const ts = Date.parse(iso);
    const today = dayStart(now);
    if (ts >= today) return TX.today;
    if (ts >= today - 86400000) return TX.yesterday.charAt(0).toUpperCase() + TX.yesterday.slice(1);
    const d = new Date(ts);
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

export const dayKeyOf = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

/** Форма слова по русскому числу: 1 минуту, 2 минуты, 5 минут, 21 минуту, 11 минут. */
export const plural = (n: number, [one, few, many]: readonly [string, string, string]): string => {
    const n10 = n % 10;
    const n100 = n % 100;
    if (n10 === 1 && n100 !== 11) return one;
    if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
    return many;
};

/**
 * Время последнего сообщения на карточке (S6 — коротко): «только что»,
 * «1 мин назад», «5 мин назад», «1 ч назад», «3 ч назад», «день назад»,
 * «7 дней назад». Будущее (часы клиента отстают) — «только что».
 */
export const timeAgo = (iso: string, now: number): string => {
    const minutes = Math.floor((now - Date.parse(iso)) / 60000);
    if (!(minutes >= 1)) return TX.justNow;
    if (minutes < 60) return `${minutes} ${TX.min} ${TX.ago}`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ${TX.hour} ${TX.ago}`;
    const days = Math.floor(hours / 24);
    return days === 1 ? TX.dayAgo : `${days} ${plural(days, TX.daysForms)} ${TX.ago}`;
};

/** Дата добавления на карточке: «12 сен» (S4.3), по местному времени. */
export const formatShortDate = (iso: string): string => {
    const d = new Date(iso);
    return `${d.getDate()} ${TX.monthsShort[d.getMonth()]}`;
};

/** Сколько последних знаков VIN хранится и показывается (решения владельца S2.1, S6). */
export const VIN_TAIL = 4;

/** Знак вместо начала VIN на экране (S6). */
export const VIN_MASK = "***";

/** VIN на экране — только маска и хвост: «***3761» (S6). */
export const maskVin = (vin: string): string => `${VIN_MASK}${vin.slice(-VIN_TAIL)}`;

export const userName = (u: IUserBrief | null | undefined): string =>
    u ? [u.first_name, u.last_name].filter(Boolean).join(" ") : "";

export const userInitials = (u: IUserBrief | null | undefined): string =>
    u ? `${u.first_name?.[0] ?? ""}${u.last_name?.[0] ?? ""}`.toUpperCase() : "";

/** Короткая метка вехи на карточке: «Калибровка дверей» → «Двери». */
export const milestoneLabel = (def: IMilestoneDef): string =>
    def.key === DOOR_CALIBRATION ? TX.doorsShort : def.title;

export type ChipState = "none" | "done" | "issue" | "miss";

/**
 * Состояние вехи на карточке. «miss» — веха не стоит, а машина уже правее её
 * этапа-хозяина (S6). В «Ремонте» пропуск не показываем: туда попадают с любого этапа.
 */
export const chipState = (
    def: IMilestoneDef,
    ms: ICarMilestone | undefined,
    car: ICar,
    stages: IStage[],
): ChipState => {
    if (ms) return ms.status === "issue" ? "issue" : "done";
    if (!def.owner_stage || car.stage === REWORK_STAGE) return "none";
    const owner = stages.find((s) => s.key === def.owner_stage);
    const cur = stages.find((s) => s.key === car.stage);
    return owner && cur && cur.order > owner.order ? "miss" : "none";
};

/** Машина ждёт инженера: колонка с «Взять в работу», а взявшего нет. */
export const isWaiting = (car: ICar, stage: IStage | undefined): boolean =>
    !!stage?.takeable && !car.taken_by;

/** Совпадение поиска по VIN: подстрока без учёта регистра. */
export const matchesVin = (vin: string, query: string): boolean =>
    !query.trim() || vin.toUpperCase().includes(query.trim().toUpperCase());
